import { DebugFlag } from '@shared/DebugFlag';
import { DocumentsStore } from '@shared/documents/DocumentsStore';
import { Events } from '@shared/Events';
import { ImagesStore } from '@shared/images/ImagesStore';
import { MarineStore } from '@shared/marine/MarineStore';
import { DocumentPropertiesService } from '@shared/services/DocumentPropertiesService';
import { FileTreeService } from '@shared/services/FileTreeService';
import { Notify } from '@shared/ui-native/Notify';
import { VehiclesStore } from '@shared/vehicles/VehiclesStore';
import { action, makeObservable, observable } from 'mobx';
import { mapStation, mapUI, mapWrapper } from '../map';
import { AlignmentStore } from '@shared/alignment/AlignmentStore';
import { processUrlParams } from '@pages/map/url-params';

export class Map2dRootStore {
    // we fetch the whole tree data, and reuse the data for multiple file trees (with some nodes filtered out as needed)
    // treeData can be slow to load and we can't block UI until its loaded, so when needed, use async getTreeData or executeWithTreeData
    @observable.ref public treeData: ITreeData = null;

    private treeDataPromise: Promise<ITreeData> = null;
    public treeDataFoldersIndex: Record<string, ITreeData> = {}; // cache for filtering

    public documentProperties: any = null;
    public readonly vehiclesStore = new VehiclesStore();
    public readonly imagesStore = new ImagesStore();
    public readonly documentsStore = new DocumentsStore();
    public readonly marineStore = new MarineStore();
    public readonly alignmentStore = new AlignmentStore();

    @observable public readonly activeAlignment = {
        modelId: -1,
        index: 0,
    } as const;
    @observable public sidebarWidth = null;

    @observable public mobileView = false;
    @observable public isInitializing = true;

    constructor() {
        makeObservable(this);

        this.mobileView = utils.mobileAndTabletCheck();
        window.addEventListener('resize', () => (this.mobileView = utils.mobileAndTabletCheck()), false);
    }

    public getTreeData = (): Promise<ITreeData> => {
        if (this.treeData) {
            return Promise.resolve(this.treeData);
        } else if (this.treeDataPromise) {
            return this.treeDataPromise;
        } else {
            try {
                this.treeDataPromise = FileTreeService.fetchFileTree() as any;
                this.treeDataPromise.then(tree => {
                    this.treeData = tree;
                    this.treeDataPromise = null;

                    this.treeDataFoldersIndex = {};
                    FileTreeService.eachPostOrder(this.treeData, 0, folder => {
                        this.treeDataFoldersIndex[folder.id] = folder;
                    });
                    return this.treeData;
                });
                return this.treeDataPromise;
            } catch (e) {
                Notify.error('Fatal error - Failed to fetch file tree');
                console.error(e);
                return Promise.reject(e);
            }
        }
    };

    executeWithTreeData = async (callback: (tree: ITreeData) => void) => {
        if (this.treeDataPromise) {
            Notify.warn('Loading necessary data...');
        }
        const tree = await this.getTreeData();
        callback(tree);
    };

    init = async () => {
        console.log('init map');
        this.isInitializing = true;
        try {
            this.documentProperties = await DocumentPropertiesService.fetchForCurrentProject();
        } catch (e) {
            console.error(e);
        }
        this.isInitializing = false;

        this.imagesStore.documentProperties = this.documentProperties;
        this.documentsStore.documentProperties = this.documentProperties;

        const promises = [];
        if (!DebugFlag.mapNoImages) {
            promises.push(this.imagesStore.fetchAndSet());
        }
        if (!DebugFlag.mapNoDocs) {
            promises.push(this.documentsStore.fetchAndSet());
        }

        setTimeout(() => {
            // processUrlParams should wait itself only for the stuff in needs, dont wait here
            processUrlParams();
        }, 500);

        // handle activeAlignmentId change
        Events.on('active-alignment-feature:removed', () => {
            this.setActiveAlignment(-1, 0);
        });
    };

    public setActiveAlignment = action((id: number, index: number) => {
        if (this.activeAlignment.modelId == id && this.activeAlignment.index == index) return;

        (this.activeAlignment.modelId as any) = id || -1;
        (this.activeAlignment.index as any) = index || 0;

        mapWrapper.featureLayers.alignments.setActiveAlignmentById(id, index);

        if (id > 0) {
            mapUI.mapPanels.panels?.models?.store?.setChecked(id, true);
            Events.trigger('mapActiveAlignmentAdded');
        } else {
            mapUI.mapPanels.panels?.models?.tree?.uncheckIds([id], false);
            Events.trigger('mapActiveAlignmentRemoved');
            mapStation.removeStationLine();
        }
    });
}
